import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { getEdges } from "../api/edgeApi";
import { requestRca, getRcaResult } from "../api/rcaApi";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 40; // ~2 dakika

export default function RcaAssistant() {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);

  const [edges, setEdges] = useState([]);
  const [loadingEdges, setLoadingEdges] = useState(true);

  const [form, setForm] = useState({
    edge_id: "",
    device_id: "",
    tag_name: "",
    timestamp: "",
    window_minutes: 15,
  });

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // RcaResultOut
  const pollTimer = useRef(null);
  const pollAttempts = useRef(0);

  useEffect(() => {
    if (!accessToken) return;
    getEdges(accessToken)
      // RCA telemetri/anomali verisine dayanıyor -- lite-tier edge (cloud_transfer_enabled=False)
      // hiç veri göndermediği için burada seçilebilir olması anlamsız.
      .then((data) => setEdges((Array.isArray(data) ? data : []).filter((e) => e.cloud_transfer_enabled)))
      .catch((err) => {
        console.error("Edge listesi alınamadı:", err);
        toast.error(t("admin.rca.edgesFailed"));
      })
      .finally(() => setLoadingEdges(false));
  }, [accessToken, t]);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  const stopPolling = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const pollResult = (requestId) => {
    pollAttempts.current = 0;
    pollTimer.current = setInterval(async () => {
      pollAttempts.current += 1;
      try {
        const data = await getRcaResult(accessToken, requestId);
        setResult(data);
        if (data.status !== "pending" || pollAttempts.current >= MAX_POLL_ATTEMPTS) {
          stopPolling();
        }
      } catch (err) {
        console.error("RCA sonucu alınamadı:", err);
        stopPolling();
        toast.error(err?.response?.data?.detail || t("admin.rca.resultFailed"));
      }
    }, POLL_INTERVAL_MS);
  };

  const handleSubmit = async () => {
    if (!form.edge_id || !form.device_id.trim() || !form.tag_name.trim() || !form.timestamp) {
      toast.error(t("admin.rca.requiredFields"));
      return;
    }

    setSubmitting(true);
    setResult(null);
    stopPolling();
    try {
      const res = await requestRca(accessToken, {
        edge_id: form.edge_id,
        device_id: form.device_id.trim(),
        tag_name: form.tag_name.trim(),
        timestamp: form.timestamp,
        window_minutes: Number(form.window_minutes) || 15,
      });
      setResult({ request_id: res.request_id, status: res.status });
      pollResult(res.request_id);
    } catch (err) {
      console.error("RCA isteği başarısız:", err);
      toast.error(err?.response?.data?.detail || t("admin.rca.requestFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const statusChip = (status) => {
    const map = {
      pending: { label: t("admin.rca.status.pending"), color: "#d29922", bg: "rgba(210,153,34,0.12)" },
      completed: { label: t("admin.rca.status.completed"), color: "var(--success)", bg: "rgba(39, 103, 73, 0.12)" },
      failed: { label: t("admin.rca.status.failed"), color: "var(--danger)", bg: "rgba(197, 48, 48, 0.12)" },
    };
    const s = map[status] || { label: status, color: "var(--text-muted)", bg: "rgba(139,148,158,0.12)" };
    return (
      <Chip
        size="small"
        label={s.label}
        sx={{ color: s.color, backgroundColor: s.bg, fontWeight: 600 }}
      />
    );
  };

  return (
    <Box sx={{ color: "var(--text-primary)", maxWidth: 720 }}>
      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
        {t("admin.rca.title")}
      </Typography>

      <Paper
        sx={{
          backgroundColor: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "8px",
          p: 3,
          mb: 3,
        }}
      >
        <Typography variant="body2" sx={{ color: "var(--text-muted)", mb: 2 }}>
          {t("admin.rca.intro")}
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }} disabled={loadingEdges}>
          <InputLabel>{t("admin.rca.edge")}</InputLabel>
          <Select
            value={form.edge_id}
            label={t("admin.rca.edge")}
            onChange={(e) => setForm({ ...form, edge_id: e.target.value })}
          >
            {edges.map((edge) => (
              <MenuItem key={edge.id} value={edge.edgeId}>
                {edge.name ?? edge.edgeId} {edge.asset?.assetId ? `— ${edge.asset.assetId}` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          margin="dense"
          label={t("admin.rca.deviceId")}
          fullWidth
          variant="outlined"
          value={form.device_id}
          onChange={(e) => setForm({ ...form, device_id: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          label={t("admin.rca.tagName")}
          fullWidth
          variant="outlined"
          value={form.tag_name}
          onChange={(e) => setForm({ ...form, tag_name: e.target.value })}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            label={t("admin.rca.anomalyTime")}
            type="datetime-local"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={form.timestamp}
            onChange={(e) => setForm({ ...form, timestamp: e.target.value })}
          />
          <TextField
            label={t("admin.rca.window")}
            type="number"
            variant="outlined"
            sx={{ width: 160 }}
            value={form.window_minutes}
            onChange={(e) => setForm({ ...form, window_minutes: e.target.value })}
          />
        </Box>

        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ backgroundColor: "var(--btn-primary)", "&:hover": { backgroundColor: "var(--btn-primary-hover)" } }}
        >
          {submitting ? t("admin.rca.submitting") : t("admin.rca.submit")}
        </Button>
      </Paper>

      {result && (
        <Paper
          sx={{
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border)",
            borderRadius: "8px",
            p: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("admin.rca.result")}
            </Typography>
            {statusChip(result.status)}
            {result.status === "pending" && <CircularProgress size={16} />}
          </Box>

          {result.status === "failed" && (
            <Typography sx={{ color: "var(--danger)", fontSize: 14 }}>
              {result.error || t("admin.rca.unknownError")}
            </Typography>
          )}

          {result.status === "completed" && (
            <>
              <Typography sx={{ color: "var(--text-primary)", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                {result.llm_explanation || t("admin.rca.noExplanation")}
              </Typography>
              {result.context_snapshot && (
                <>
                  <Divider sx={{ my: 2, borderColor: "var(--border)" }} />
                  <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>
                    {t("admin.rca.rawContext")}
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1.5,
                      backgroundColor: "var(--bg-surface)",
                      border: "1px solid var(--border)",
                      borderRadius: 1,
                      fontSize: 12,
                      color: "var(--text-muted)",
                      overflowX: "auto",
                    }}
                  >
                    {JSON.stringify(result.context_snapshot, null, 2)}
                  </Box>
                </>
              )}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
